import React, { useState } from "react";
import SaveButtonWithModal from "../SaveButtonWithModel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FiEye, FiEyeOff } from "react-icons/fi";

const languages = [
  "Black African", "While", "Coloured", "Indian",
];
const gender = [
  "Male", "Female", "Other",
];
const person = [
  "With Disability", "Youth", "-",
];

// Validation helpers
const isValidName = (name) => {
  if (!name) return false;
  const words = name.trim().split(/\s+/);
  return words.length <= 8 && /^[A-Za-z\s]+$/.test(name);
};
const isValidSurname = (surname) => {
  if (!surname) return false;
  const words = surname.trim().split(/\s+/);
  return words.length <= 8 && /^[A-Za-z\s]+$/.test(surname);
};
// Accept Date object or string in YYYY-MM-DD
const isValidDateOfBirth = (dob) => {
  if (!dob) return false;
  if (dob instanceof Date && !isNaN(dob)) return true;
  return /^\d{4}-\d{2}-\d{2}$/.test(dob);
};
const isValidId = (id) => {
  return id && /^[A-Za-z0-9]+$/.test(id) && id.length >= 5;
};
const isValidMobile = (mobile) => {
  return /^\+?[0-9\s\-]{7,20}$/.test(mobile);
};
const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};
const isValidPassword = (password) => {
  return /^(?=.*[A-Za-z])(?=.*\d).{6,}$/.test(password);
};
const isValidWhatsapp = (whatsapp) => {
  return /^\+?[0-9\s\-]{7,20}$/.test(whatsapp);
};
const isValidPhysicalAddress = (address) => {
  return address && address.length >= 5 && address.length <= 100;
};
const isValidPostalCode = (code) => {
  return /^\d{4,6}$/.test(code);
};

const handleSelect = (option, formData, onFormDataChange) => {
  onFormDataChange({ ...formData, typeOfId: option });
};

const requiredFields = [
  "firstName", "surname", "dateOfBirth", "typeOfId", "idNumber",
  "mobile", "email", "password", "whatsapp", "physicalAddress", "postalCode"
];

const fieldValidators = {
  firstName: isValidName,
  surname: isValidSurname,
  dateOfBirth: isValidDateOfBirth,
  typeOfId: (val) => !!val && val.trim() !== "",
  idNumber: isValidId,
  mobile: isValidMobile,
  email: isValidEmail,
  password: isValidPassword,
  whatsapp: isValidWhatsapp,
  physicalAddress: isValidPhysicalAddress,
  postalCode: isValidPostalCode,
};

const fieldErrorMessages = {
  firstName: "First Name: max 8 words, only letters.",
  surname: "Surname: max 8 words, only letters.",
  dateOfBirth: "Date required (YYYY-MM-DD).",
  typeOfId: "Type of ID is required.",
  idNumber: "Alphanumeric, at least 5 characters.",
  mobile: "Enter a valid phone number (may start with +).",
  whatsapp: "Enter a valid WhatsApp number (may start with +).",
  email: "Invalid email address.",
  password: "Min 6 chars, 1 letter & 1 number.",
  physicalAddress: "5-100 characters.",
  postalCode: "4-6 digits.",
};
const CustomDateDiv = React.forwardRef(({ value, onClick }, ref) => (
  <div
    onClick={onClick}
    ref={ref}
    className="xl:w-[390px]  lg:w-[330px] md:w-[250px] w-[115px] h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] text-[#979797] border rounded-[5px] bg-[#E7EBEB] border-[#E7EBEB] cursor-pointer"
  >
    {value || "YYYY-MM-DD"}
  </div>
));
const PersonalInfoForm = ({
  formData,
  onFormDataChange,
  race,
  onRaceChange,
  gender: selectedGender,
  onGenderChange,
  disability,
  onDisabilityChange,
  onSave
}) => {
  const [showModal, setShowModal] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  // Validate all fields on change and on submit
  const validateAllFields = (data = formData) => {
    let errors = {};
    requiredFields.forEach((field) => {
      const value = data[field];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        errors[field] = "This field is required.";
      } else if (fieldValidators[field] && !fieldValidators[field](value)) {
        errors[field] = fieldErrorMessages[field];
      }
    });
    return errors;
  };

  // Real-time validation per field
  const handleChange = (e) => {
    const { name, value } = e.target;
    onFormDataChange({ ...formData, [name]: value });

    let error = "";
    if (!value || value.trim() === "") {
      error = "This field is required.";
    } else if (fieldValidators[name] && !fieldValidators[name](value)) {
      error = fieldErrorMessages[name];
    }
    setFieldErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Date picker change handler
  const handleDateChange = (date) => {
    onFormDataChange({ ...formData, dateOfBirth: date });
    let error = "";
    if (!date) {
      error = "This field is required.";
    } else if (!isValidDateOfBirth(date)) {
      error = fieldErrorMessages.dateOfBirth;
    }
    setFieldErrors((prev) => ({ ...prev, dateOfBirth: error }));
  };
  const handleSelect = (option) => {
    onFormDataChange((prev) => ({ ...prev, typeOfId: option }));
    setIsOpen(false); // Close dropdown after selection
  };

  // Check for empty gender/disability
  const handleGenderChange = (val) => {
    onGenderChange(val);
    if (!val) {
      setFieldErrors((prev) => ({ ...prev, gender: "Please select gender." }));
    } else {
      setFieldErrors((prev) => {
        const { gender, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleDisabilityChange = (val) => {
    onDisabilityChange(val);
    if (!val) {
      setFieldErrors((prev) => ({ ...prev, disability: "Please select disability/ability." }));
    } else {
      setFieldErrors((prev) => {
        const { disability, ...rest } = prev;
        return rest;
      });
    }
  };

  // On Save: validate all
  const handleSave = () => {
    const errors = validateAllFields();
    if (!selectedGender) errors.gender = "Please select gender.";
    if (!disability) errors.disability = "Please select disability/ability.";
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setSubmitError("Please fix the errors below.");
      return;
    }
    setSubmitError("");
    setShowModal(true);
    onSave({
      ...formData,
      race,
      gender: selectedGender,
      disability,
    });
  };

  return (
    <div className="mx-auto  md:mt-10 mt-5 ">
      <h2 className="text-center font-bold text-[10px] md:text-[32px] text-black">
        SECTION A: PERSONAL INFORMATION
      </h2>
      <p className="text-center text-[8px] md:text-[32px] text-black md:mb-8 mb-3">
        Individual Applications Only
      </p>
      {submitError && <div className="text-red-600 text-center mb-2 text-sm">{submitError}</div>}
      {/* FORM */}
      <form onSubmit={e => e.preventDefault()}>
        <div>
          <div className="flex flex-col md:gap-y-6 gap-2 w-full">
            {/* Row 1 */}
            <div className="flex md:flex-row  md:gap-2 gap-1">
              {/* First Name */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black">First Name*</label>
                <input
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Lewis"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.firstName && <div className="text-red-500 text-xs mt-1">{fieldErrors.firstName}</div>}
              </div>
              {/* Surname */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black">Surname*</label>
                <input
                  name="surname"
                  value={formData.surname}
                  onChange={handleChange}
                  placeholder="Chauke"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.surname && <div className="text-red-500 text-xs mt-1">{fieldErrors.surname}</div>}
              </div>
              {/* Date of Birth */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black">Date of Birth*</label>
                <DatePicker
                  selected={formData.dateOfBirth ? (formData.dateOfBirth instanceof Date ? formData.dateOfBirth : new Date(formData.dateOfBirth)) : null}
                  onChange={handleDateChange}
                  dateFormat="yyyy-MM-dd"
                  name="dateOfBirth"
                  required
                  maxDate={new Date()}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  customInput={<CustomDateDiv />}
                />


                {fieldErrors.dateOfBirth && <div className="text-red-500 text-xs mt-1">{fieldErrors.dateOfBirth}</div>}
              </div>
            </div>
            {/* Row 2 */}
            <div className="flex md:flex-row md:gap-2 gap-1">
              {/* Type of ID */}
              <div className="w-full relative">
                <div className="flex justify-between items-center cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
                  <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black">Type of ID*</label>
                </div>

                <div
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black flex items-center justify-between cursor-pointer"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  {formData.typeOfId || "Passport"}

                  <img
                    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1746389452/Expand_down_19_ylkeiy.png"
                    alt="Dropdown icon"
                    className="md:w-[28px] md:h-[28px] w-[10px] h-[10px]"
                  />
                </div>

                {isOpen && (
                  <div className="absolute z-10 w-full bg-white border rounded-md text-black mt-1 max-h-60 overflow-auto shadow-lg">
                    {["Identity card", "Passport", "Drivers License "].map((option, index) => (
                      <div
                        key={index}
                        onClick={() => handleSelect(option, formData, onFormDataChange)}
                        className="px-3 py-2 text-[8px] md:text-[24px] hover:bg-[#E7EBEB] cursor-pointer"
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}

                {fieldErrors.typeOfId && <div className="text-red-500 text-xs mt-1">{fieldErrors.typeOfId}</div>}
              </div>

              {/* ID Number */}
              <div className="w-full relative">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black">ID Number*</label>
                <input
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleChange}
                  required
                  placeholder="ID123456789"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-[#979797] cursor-pointer"
                />
                {fieldErrors.idNumber && <div className="text-red-500 text-xs mt-1">{fieldErrors.idNumber}</div>}
              </div>
              {/* Mobile */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black">Mobile*</label>
                <input
                  name="mobile"
                  type="text"
                  value={formData.mobile}
                  onChange={handleChange}
                  placeholder="0XXXXXXXXXXXX"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] border-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.mobile && <div className="text-red-500 text-xs mt-1">{fieldErrors.mobile}</div>}
              </div>
            </div>
            {/* Row 3 */}
            <div className="flex md:flex-row md:gap-2 gap-1">
              {/* Email */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black">Email*</label>
                <input
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="ent@email.com"
                  required
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.email && <div className="text-red-500 text-xs mt-1">{fieldErrors.email}</div>}
              </div>
              {/* Password */}
              <div className="w-full">
  <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black">Password*</label>
  <div className="relative">
    <input
      name="password"
      type={showPassword ? "text" : "password"}
      value={formData.password}
      onChange={handleChange}
      required
      placeholder="xyz123"
      className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black border-[#E7EBEB] bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
    />
    <button
      type="button"
      onClick={togglePasswordVisibility}
      className="absolute right-3 md:text-[30px] top-1/2 transform -translate-y-1/2 text-[#979797]"
    >
      {showPassword ? <FiEye /> : <FiEyeOff />}
    </button>
  </div>
  {fieldErrors.password && <div className="text-red-500 text-xs mt-1">{fieldErrors.password}</div>}
</div>
              {/* WhatsApp */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black">WhatsApp*</label>
                <input
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  required
                  placeholder="0XXXXXXXXXXXX"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.whatsapp && <div className="text-red-500 text-xs mt-1">{fieldErrors.whatsapp}</div>}
              </div>
            </div>
            {/* Row 4 */}
            <div className="flex md:flex-row md:gap-2 gap-1 w-2/3 md:w-2/3">
              {/* Physical Address */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black">Physical Address*</label>
                <input
                  name="physicalAddress"
                  value={formData.physicalAddress}
                  onChange={handleChange}
                  required
                  placeholder="1Farm road, Gauteng"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.physicalAddress && <div className="text-red-500 text-xs mt-1">{fieldErrors.physicalAddress}</div>}
              </div>
              {/* Postal Code */}
              <div className="w-full">
                <label className="block font-bold text-[8px] md:text-[32px] px-2 text-black">Postal Code*</label>
                <input
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  required
                  placeholder="0000"
                  className="w-full h-[30px] md:h-[71px] p-1 md:p-2 pl-3 md:pl-4 text-[8px] md:text-[32px] border rounded-[5px] text-black bg-[#E7EBEB] placeholder:text-[#979797] md:placeholder:text-[#979797] placeholder:text-[8px] md:placeholder:text-[32px] placeholder:text-black"
                />
                {fieldErrors.postalCode && <div className="text-red-500 text-xs mt-1">{fieldErrors.postalCode}</div>}
              </div>
            </div>
          </div>
        </div>
      </form>
      {/* Race Section */}
      <label className="block font-normal md:mb-2 mb-1 md:mt-6 mt-4 text-[6px] md:pl-2 md:text-[32px] text-black">
        <strong>Race</strong> (*Optional)
      </label>
      <div className="grid grid-cols-3 md:grid-cols-3 gap-2 mt-2">
        {languages.map((lang, i) => {
          const isActive = race === lang;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onRaceChange(lang)}
              className="flex items-center justify-between w-full h-[25px] md:h-[71px] bg-[#E7EBEB] border rounded md:px-4 px-2 "
            >
              <span className="font-medium  text-[6px] md:text-[32px] md:pb-3">{lang}</span>
              <span
                className={`md:w-[55px] md:h-[55px] h-[22px] w-[22px] rounded-full ${isActive ? 'bg-[#03897B] md:border-[8px] border-[4px]  border-white' : 'md:border-[8px] border-[4px] border-white bg-transparent'}`}
              />
            </button>
          );
        })}
      </div>
      {/* Gender Section */}
      <div className="md:mt-8 mt-4">
        <label className="block font-bold md:mb-1 mb-1 md:pl-2  text-[6px] md:text-[32px] text-black">Gender*</label>
        <div className="grid grid-cols-3 md:grid-cols-3 gap-2">
          {gender.map((g, i) => {
            const isActive = selectedGender === g;
            return (
              <button
                key={i}
                type="button"
                onClick={() => handleGenderChange(g)}
                className="flex items-center justify-between w-full h-[25px] md:h-[71px] bg-[#E7EBEB] border rounded px-4 "
              >
                <span className="font-medium text-[6px] md:text-[32px] md:pb-3">{g}</span>
                <span
                  className={`md:w-[55px] md:h-[55px] h-[22px] w-[22px] rounded-full ${isActive ? 'bg-[#03897B] md:border-[7px] border-[4px]  border-white' : 'md:border-[8px] border-[4px] border-white bg-transparent'}`}
                />
              </button>
            );
          })}
        </div>
        {fieldErrors.gender && <div className="text-red-500 text-xs mt-1">{fieldErrors.gender}</div>}
      </div>
      {/* Disability Section */}
      <div className="md:mt-8 mt-4">
        <label className="block font-bold md:mb-2 md:pl-2  mb-1  text-[6px] md:text-[32px] text-black">
          Person with Disability / Ability*
        </label>
        <div className="grid grid-cols-3 md:grid-cols-3 gap-2">
          {person.map((p, i) => {
            const isActive = disability === p;
            return (
              <button
                key={i}
                type="button"
                onClick={() => handleDisabilityChange(p)}
                className="flex items-center justify-between w-full h-[25px] md:h-[71px] bg-[#E7EBEB] border rounded px-4 "
              >
                <span className="font-medium   text-[6px]  md:text-[32px] md:pb-3">{p}</span>
                <span
                  className={`md:w-[55px] md:h-[55px] h-[22px] w-[22px] rounded-full ${isActive ? 'bg-[#03897B] md:border-[7px] border-[4px]  border-white' : 'md:border-[8px] border-[4px] border-white bg-transparent'}`}
                />
              </button>
            );
          })}
        </div>
        {fieldErrors.disability && <div className="text-red-500 text-xs mt-1">{fieldErrors.disability}</div>}
        <h1 className="md:text-[18px] text-[6px] font-normal md:mt-1 italic text-black text-center md:mr-36">*Youth -ages 18-28 years</h1>
      </div>
      {/* Save button */}
      <SaveButtonWithModal />
      {/* Save Modal */}
      {showModal && (
        <SaveButtonWithModal
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

export default PersonalInfoForm;